<script lang="ts">
import type { Colors, Variants, Sizes, SlotInterface, ButtonProps } from 'types';

export interface AlertProps {
  color?: Colors;
  variant?: Variants;
  size?: Sizes;
  icon?: string;
  title?: string;
  description?: string;

  /* Enables component to be closed */
  close?: boolean;

  /* Function(s) or event(s) that will be executed on close */
  onClose?: ((event: MouseEvent) => void | Promise<void>) | null;

  /* The icon that should be used as the closing icon */
  closeIcon?: string;

  /* A list of buttons that should be displayed in the `actions` slot */
  buttons?: ButtonProps[];

  /* Enables progress bar */
  progressBar?: boolean;

  /* Sets progress duration */
  progressDuration?: number;
  class?: string;
}

export interface AlertSlots extends SlotInterface<{
  leading?(props?: {}): unknown;
  header?(props?: {}): unknown;
  default?(props?: {}): unknown;
  footer?(props?: {}): unknown;
  trailing?(props?: {}): unknown;
}> {}
</script>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useConditionalSlots } from 'composables/useConditionalSlots';

const props = withDefaults(defineProps<AlertProps>(), {
  color: 'neutral',
  variant: 'soft',
  size: 'md',
  progressDuration: 10000
});

const emit = defineEmits<AlertEmit>();

type AlertEmit = (e: 'close', event: MouseEvent) => void;

const headingTag = computed(() => {
  switch (props.size) {
    case 'xs':
      return 'h6';
    case 'sm':
      return 'h5';
    case 'md':
      return 'h5';
    case 'lg':
      return 'h4';
    case 'xl':
      return 'h3';
    default:
      return 'h3';
  }
});

const isClosable = computed(() => {
  return props.progressBar || props.close;
});

function handleClose(event: MouseEvent): void {
  if (props.onClose) {
    props.onClose(event);
  }
  emit('close', event);
}

const progress = ref(0);
const isPaused = ref(false);
let startTime = Date.now();
let elapsedTime = 0;
let rafId: number | null = null;

const duration = computed(() => props.progressDuration);

function updateProgress(): void {
  if (isPaused.value) {
    rafId = requestAnimationFrame(updateProgress);
    return;
  }
  const now = Date.now();
  const delta = now - startTime;
  elapsedTime += delta;
  startTime = now;
  progress.value = Math.min((elapsedTime / duration.value) * 100, 100);
  if (progress.value >= 100) {
    handleClose(new MouseEvent('click'));
    return;
  }
  if (progress.value < 100) {
    rafId = requestAnimationFrame(updateProgress);
  }
}

function pause(): void {
  isPaused.value = true;
}

function resume(): void {
  isPaused.value = false;
  startTime = Date.now();
}

onMounted(() => {
  if (props.progressBar) {
    updateProgress();
  }
});

onUnmounted(() => {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
  }
});

const classes = computed(() => [
  'e-alert',
  props.color && `e-alert-color-${props.color}`,
  props.variant && `e-alert-variant-${props.variant}`,
  props.size && `e-alert-size-${props.size}`,
  props.class
]);

const elRef = ref<HTMLElement | null>(null);

const {
  leading: hasLeading,
  header: hasHeader,
  default: hasDefault,
  footer: hasFooter,
  trailing: hasTrailing
} = useConditionalSlots<AlertSlots>(elRef, ['leading', 'header', 'default', 'footer', 'trailing']);
</script>

<template>
  <section
    ref="elRef"
    :class="classes"
    @mouseenter="pause"
    @mouseleave="resume"
  >
    <div v-if="icon || hasLeading">
      <slot name="leading">
        <e-icon
          v-if="icon"
          :name="icon"
        />
      </slot>
    </div>
    <div class="e-alert-content">
      <header
        v-if="title || hasHeader"
        class="e-alert-header"
      >
        <slot name="header">
          <e-heading
            as="h1"
            class="e-alert-heading h4"
            :type="headingTag"
            :title="title"
          />
        </slot>
      </header>

      <div v-if="hasDefault || description">
        <slot>
          <e-text class="e-alert-description">
            {{ description }}
          </e-text>
        </slot>
      </div>

      <div v-if="hasFooter">
        <slot name="footer">
          <div
            v-if="buttons && buttons.length"
            class="e-alert-actions"
          >
            <e-button
              v-for="(button, index) in buttons"
              :key="`alert-action-button-${index}`"
              class="e-alert-action"
              v-bind="{ ...button, size: 'xs' }"
            />
          </div>
        </slot>
      </div>
    </div>
    <div v-if="isClosable || hasTrailing">
      <slot name="trailing">
        <e-button
          v-if="isClosable"
          as="button"
          :icon="closeIcon ?? 'close'"
          square
          size="xs"
          variant="ghost"
          class="e-alert-close"
          :color="variant === 'solid' ? 'neutral' : color"
          :class="variant === 'solid' ? '!text-white' : ''"
          @click="handleClose"
        />
      </slot>
    </div>
    <div
      v-if="progressBar"
      class="e-alert-progress-bar"
      :style="{ width: progress + '%' }"
    />
  </section>
</template>
